import React, { FC, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import _ from 'lodash';
import { Delete as Trash, Edit } from '@material-ui/icons/';
import { TableColumnsData } from './types';
import { RootState } from '../../../../../redux/root';
import TableHeaderCell from './Header/TableHeaderCell';
import TableCell from './TableCell';
import DetailsIcon from './DetailsIcon';
import { generateComparator } from '../../../utils';
import NoRecordsFound from './NoRecordsFound';
import MobileFilter from '../../../../../pages/Responsive/MobileFilter';
import { setShowDrawerContent } from '../../../../../redux/slices/generalSlice';
import TableRow from './TableRow';
import TableCellHyperLink from './TableCellHyperLink';
import { Loader } from '../../../../../atoms';

const Table: FC<TableColumnsData> = ({
  collection,
  headers,
  gridTemplateCols,
  setSelectedItemInSlice = undefined,
  onDelete = undefined,
  onEdit = undefined,
  filterMeta,
  showFilters = true,
  isLoading = false,
}) => {
  const dispatch = useDispatch();
  const scrollRef = useRef<HTMLDivElement>();
  const [sortedCollection, setSortedCollection] = useState(collection);
  const [isScrollbarVisible, setIsScrollbarVisible] = useState<boolean>(false);
  const { tableSortingDirection, tableSortingKey } = useSelector((state: RootState) => state.general);

  useEffect(() => {
    if (scrollRef.current) {
      setIsScrollbarVisible(scrollRef.current.scrollHeight > scrollRef.current.clientHeight);
    }
  }, [sortedCollection]);
  useEffect(() => {
    if (tableSortingDirection === 'NONE') {
      setSortedCollection(collection);
    }
  }, [tableSortingKey, collection, tableSortingDirection]);

  useEffect(() => {
    const _sortedItem = [...collection];
    _sortedItem.sort(generateComparator(tableSortingDirection, tableSortingKey));
    if (tableSortingDirection !== 'NONE') {
      setSortedCollection(_sortedItem);
    }
  }, [tableSortingDirection, collection]);

  // See if better to pass function from outside
  const handleDetailClick = (item: any, allowVisibility) => {
    if (!allowVisibility) {
      return;
    }
    if (setSelectedItemInSlice) {
      setSelectedItemInSlice(item);
    }
    dispatch(setShowDrawerContent(true));
  };

  return (
    <TableRoot>
      <TableHeaderRow gridTemplateCols={gridTemplateCols} isScrollVisible={isScrollbarVisible}>
        {headers.map(({ label, sortingKey, hideFilter, itemName, customFilter }, index) => (
          <TableHeaderCell
            key={index}
            label={label}
            itemName={itemName}
            sortingKey={sortingKey}
            hideFilter={hideFilter}
            showFilters={showFilters}
            customFilter={customFilter}
          />
        ))}
      </TableHeaderRow>
      <TableContentContainer ref={scrollRef} id="mainTable">
        {isLoading && (
          <LoaderWrapper>
            <Loader size={36} thickness={3.5} />
            <LoaderLabel>Loading…</LoaderLabel>
          </LoaderWrapper>
        )}
        {!isLoading && sortedCollection.length === 0 && <NoRecordsFound />}
        <MobileFilter filterMeta={filterMeta} />
        {!isLoading && sortedCollection.map((collectionItem, index) => {
          const allowVisibility = collectionItem.status !== 'Sent' && collectionItem.status !== 'Waiting on Processing';
          return (
            <TableRow key={index} index={index} gridTemplateCols={gridTemplateCols}>
              {headers.map(({ label, sortingKey, theKey, hyperLink, isHtml }, index) => {
                if (index === headers.length - 1) {
                  return (
                    <TableCell header={label} key={index}>
                      <Icons>
                        {setSelectedItemInSlice && (
                          <div onClick={e => handleDetailClick(collectionItem, allowVisibility)}>
                            <DetailsIcon allowVisibility={allowVisibility} />
                          </div>
                        )}
                        {onEdit && (
                          <IconWrapper onClick={e => onEdit(collectionItem)}>
                            <Edit />
                          </IconWrapper>
                        )}
                        {onDelete && (
                          <IconWrapper onClick={e => onDelete(collectionItem)}>
                            <Trash />
                          </IconWrapper>
                        )}
                      </Icons>
                    </TableCell>
                  );
                }
                const _theKey = sortingKey || theKey;
                if (hyperLink) {
                  return (
                    <TableCellHyperLink
                      visibleFilename={collectionItem.permittingVisibleFilename}
                      base64data={collectionItem.permittingReportBase64}
                    />
                  );
                }
                return <TableCell isHtml={isHtml} key={index} header={label} value={_.get(collectionItem, _theKey)} />;
              })}
            </TableRow>
          );
        })}
      </TableContentContainer>
    </TableRoot>
  );
};

export default Table;

const Icons = styled.div`
  display: flex;
  justify-content: space-around;
  width: 100%;
  gap: 9px;
`;

const TableRoot = styled.div`
  width: 100%;
  min-width: 0;
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
  flex-direction: column;
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-xs);
  overflow: hidden;
  margin: 0 0 10px;
  @media only screen and (max-width: 540px) {
    margin: 0 0 8px;
  }
`;

const TableContentContainer = styled.div`
  flex-grow: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  max-height: 80vh;
`;

const TableHeaderRow = styled.div<{
  gridTemplateCols: string;
  isScrollVisible: boolean;
}>`
  display: grid;
  grid-template-columns: ${({ gridTemplateCols }) => gridTemplateCols};
  background: var(--table-row-header);
  border-bottom: 1px solid var(--border);
  padding-right: ${({ isScrollVisible }) => (isScrollVisible ? '16px' : '0px')};
  position: sticky;
  top: 0;
  z-index: 2;

  @media only screen and (max-width: 840px) {
    display: none;
  }
`;

const IconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  color: var(--neutral-500);
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;

  svg {
    font-size: 18px;
  }

  &:hover {
    color: var(--primary-600);
    background: var(--primary-50);
  }
`;

const LoaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 12px;
  padding: 72px 24px;
  color: var(--neutral-500);
`;

const LoaderLabel = styled.div`
  font-size: 0.8125rem;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: var(--neutral-500);
`;
